import { Shield, TreePine, Award, Briefcase } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { getContactPath } from '../utils/routes'
import { useSiteImages } from '../hooks/useSiteImages'
import { useHeroes } from '../hooks/useHeroes'
import Button from '../components/ui/Button'
import SectionHeading from '../components/ui/SectionHeading'
import PlaceholderImage from '../components/ui/PlaceholderImage'
import PeopleCarousel from '../components/PeopleCarousel'
import SEOHead from '../components/SEOHead'

const valueIcons = [Shield, TreePine, Award, Briefcase]

export default function About() {
  const { locale, t } = useLocale()
  const { siteImages } = useSiteImages()
  const { heroes } = useHeroes()

  const teamPeople =
    heroes.length > 0
      ? heroes.map((h) => ({
          name: h.name,
          role: h.role[locale],
          bio: h.bio[locale],
          photo: h.photo,
        }))
      : t.heroes.members.map((m) => ({
          name: m.name,
          role: m.role,
          bio: m.bio,
          photo: '',
        }))

  return (
    <>
      <SEOHead title={t.about.metaTitle} description={t.about.metaDescription} />

      {/* Hero / Intro */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <h1 className="mb-4">{t.about.heroTitle}</h1>
              <p className="text-text-secondary text-lg leading-relaxed">
                {t.about.heroSubtitle}
              </p>
            </div>
            <div className="order-1 md:order-2">
              {siteImages?.hero ? (
                <img
                  src={siteImages.hero}
                  alt="Magic Harghita Resort"
                  width={800}
                  height={600}
                  className="w-full h-64 md:h-80 object-cover rounded-card"
                  loading="eager"
                  fetchPriority="high"
                />
              ) : (
                <PlaceholderImage
                  label="Resort / Landscape"
                  variant="resort"
                  className="w-full h-64 md:h-80 rounded-card"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-surface">
        <div className="container-max max-w-3xl">
          <SectionHeading title={t.about.storyTitle} centered={false} />
          <div className="space-y-4 text-text-secondary leading-relaxed">
            <p>{t.about.storyP1}</p>
            <p>{t.about.storyP2}</p>
            <p>{t.about.storyP3}</p>
            <p>{t.about.storyP4}</p>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="section-padding bg-white">
        <div className="container-max">
          {/* Mission */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="mb-4">{t.about.missionTitle}</h2>
            <p className="text-text-secondary text-lg leading-relaxed italic">
              "{t.about.missionText}"
            </p>
          </div>

          {/* Values */}
          <SectionHeading title={t.about.valuesTitle} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.about.values.map((value, i) => {
              const Icon = valueIcons[i]
              return (
                <div key={i} className="text-center p-6">
                  <div className="w-14 h-14 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={28} className="text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-medium text-lg mb-2">{value.title}</h3>
                  <p className="text-text-secondary text-sm">{value.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <SectionHeading title={t.about.teamTitle} subtitle={t.about.teamSubtitle} />
          <PeopleCarousel people={teamPeople} />
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-surface">
        <div className="container-max max-w-2xl">
          <SectionHeading title={t.about.timelineTitle} />
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-border md:-translate-x-0.5" />

            {t.about.timeline.map((item, i) => (
              <div
                key={i}
                className={`relative flex items-start gap-4 md:gap-8 mb-8 last:mb-0 ${
                  i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Dot */}
                <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary rounded-full -translate-x-1.5 md:-translate-x-1.5 mt-1.5 z-10" />

                {/* Content */}
                <div className={`ml-10 md:ml-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <span className="text-primary font-heading font-semibold text-xl">
                    {item.year}
                  </span>
                  <p className="text-text-secondary mt-1">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-secondary to-secondary-hover">
        <div className="container-max px-4 md:px-8 text-center">
          <h2 className="text-white mb-4">{t.about.ctaText}</h2>
          <Button to={getContactPath(locale)} size="lg">
            {t.about.ctaButton}
          </Button>
        </div>
      </section>
    </>
  )
}
